import React, { forwardRef } from 'react';
import { OutlinedInput } from '@material-ui/core';
import { useDsTokens } from '../theme/useDsTokens';
import { cn } from '../utils/cn';
import { DsCommonProps, DsValidationProps, InputRef } from './types';

export interface TextareaProps extends DsCommonProps, DsValidationProps {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  name?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  minRows?: number;
  maxRows?: number;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    id,
    className,
    disabled,
    error,
    required,
    value,
    defaultValue,
    placeholder,
    name,
    readOnly,
    fullWidth = true,
    minRows = 3,
    maxRows,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    onChange,
    onBlur,
    onFocus,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();

  return (
    <OutlinedInput
      id={id}
      inputRef={ref}
      className={cn(className)}
      data-testid={testId}
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      name={name}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      fullWidth={fullWidth}
      error={error}
      multiline
      minRows={minRows}
      maxRows={maxRows}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid ?? (error ? true : undefined)}
      onChange={
        onChange
          ? (onChange as unknown as React.ChangeEventHandler<HTMLInputElement>)
          : undefined
      }
      onBlur={
        onBlur
          ? (onBlur as unknown as React.FocusEventHandler<HTMLInputElement>)
          : undefined
      }
      onFocus={
        onFocus
          ? (onFocus as unknown as React.FocusEventHandler<HTMLInputElement>)
          : undefined
      }
      style={{
        backgroundColor: readOnly ? tokens.surfaceSunken : tokens.surfaceInput,
        borderRadius: tokens.radiusMd,
        fontSize: tokens.fontSizeBody,
        color: tokens.textPrimary,
        transition: `border-color ${tokens.motionDurationStandard}ms ${tokens.motionEasingStandard}`,
      }}
      inputProps={{
        style: {
          padding: `${tokens.space1}px ${tokens.space2}px`,
          resize: 'vertical',
        },
      }}
    />
  );
});

Textarea.displayName = 'Textarea';

export type { InputRef };
